import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useAuth } from "../../../firebase/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiUrl } from "../../../config/api";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import UploadTab from "./tabs/upload/UploadMenu";
import DatabaseView from "./tabs/products/DatabaseView";
import EditHistory from "./tabs/history/ProductLog";
import ProductDuplicatesPanel from "./tabs/duplicates/ProductDuplicatesPanel";
// Tab configuration for cleaner code

function ProductPage() {
  const { currentUser, getUserProfile, hasRole } = useAuth();
  const location = useLocation();
  const profile = getUserProfile();
  const [loading, setLoading] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Read initial tab from navigation state (e.g. when coming back from ProductProperties)
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "database",
  );

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 12,
    total: 0,
    pages: 1,
  });
  const [categoryFilter, setCategoryFilter] = useState(null); // category_id or null
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);

  const [editLogs, setEditLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
  });
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Ref to abort in-flight search/filter requests when a newer one starts
  const searchAbortControllerRef = useRef(null);

  const getLangFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  }, [location.search]);

  const [language, setLanguage] = useState(getLangFromUrl);
  const navigate = useNavigate();

  const TAB_DEFAULT = {
    database: {
      id: "database",
      iconPath:
        "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
      labelEn: "Products",
      labelZh: "產品庫",
      descEn: "View and manage all products",
      descZh: "查看和管理所有產品",
    },
  };

  const TAB_CONFIG_ADMIN = useMemo(() => {
    if (!hasRole) return TAB_DEFAULT;
    return {
      ...TAB_DEFAULT,
      ...(hasRole([
        "product.create",
        "knowledge.faq.publish",
        "knowledge.video.publish",
      ]) && {
        upload: {
          id: "upload",
          iconPath:
            "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
          labelEn: "Upload",
          labelZh: "上傳",
          descEn: "Upload products & publish videos",
          descZh: "上傳產品和發佈影片",
        },
      }),
      ...(hasRole("product.logs.read") && {
        logs: {
          id: "logs",
          iconPath:
            "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
          labelEn: "History",
          labelZh: "歷史",
          descEn: "Track all changes",
          descZh: "追蹤所有變更",
        },
      }),
      ...(hasRole("product.merge") && {
        duplicates: {
          id: "duplicates",
          iconPath:
            "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
          labelEn: "Duplicates",
          labelZh: "重複產品",
          descEn: "Detect & merge duplicate products",
          descZh: "偵測並合併重複產品",
        },
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRole]);

  useEffect(() => {
    setLanguage(getLangFromUrl());
  }, [getLangFromUrl]);

  // Guard: if active tab is not in the resolved config (e.g. role not loaded yet), fall back to "database"
  useEffect(() => {
    if (!(activeTab in TAB_CONFIG_ADMIN)) {
      setActiveTab("database");
    }
  }, [TAB_CONFIG_ADMIN, activeTab]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [language, location.search, navigate]);

  useEffect(() => {
    if (!currentUser) {
      navigate({ pathname: "/login", search: `?lang=${language}` });
    }
  }, [currentUser, navigate, language]);

  const fetchProductsAndLogs = async ({
    page = pagination.page,
    per_page = pagination.per_page,
    category_id = categoryFilter,
    q = searchQuery,
  } = {}) => {
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const config = { headers: { Authorization: `Bearer ${token}` }, timeout: 30_000 };

      const params = new URLSearchParams({ page, per_page });
      if (category_id) params.set("category_id", category_id);
      if (q) params.set("q", q);

      const productsPromise = axios.get(
        `${getApiUrl()}/api/v2/products/?${params.toString()}`,
        config,
      );
      const logsParams = new URLSearchParams({
        page: 1,
        per_page: logsPagination.per_page,
      });
      const logsPromise = axios.get(
        `${getApiUrl()}/api/v2/logs/product-update-logs?${logsParams.toString()}`,
        config,
      );
      const categoriesPromise =
        categories.length === 0
          ? axios.get(`${getApiUrl()}/api/v2/products/categories`, config)
          : Promise.resolve(null);

      const [productsResult, logsResult, categoriesResult] =
        await Promise.allSettled([
          productsPromise,
          logsPromise,
          categoriesPromise,
        ]);

      if (productsResult.status === "fulfilled") {
        const data = productsResult.value.data;
        console.log("Fetched products data:", data);
        setProducts(data.products || []);
        if (data.pagination) setPagination(data.pagination);
      } else {
        console.error("Error fetching products:", productsResult.reason);
        setProducts([]);
      }

      if (logsResult.status === "fulfilled") {
        const data = logsResult.value.data;
        setEditLogs(data.logs || (Array.isArray(data) ? data : []));
        if (data.pagination) setLogsPagination(data.pagination);
      } else {
        console.error("Error fetching logs:", logsResult.reason);
        setEditLogs([]);
      }

      if (categoriesResult.status === "fulfilled" && categoriesResult.value) {
        console.log("Fetched categories data:", categoriesResult.value.data);
        setCategories(categoriesResult.value.data?.data || []);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setProducts([]);
      setEditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async ({
    page = logsPagination.page,
    per_page = logsPagination.per_page,
  } = {}) => {
    setLoadingLogs(true);
    try {
      const token = await currentUser.getIdToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const params = new URLSearchParams({ page, per_page });
      const { data } = await axios.get(
        `${getApiUrl()}/api/v2/logs/product-update-logs?${params.toString()}`,
        config,
      );

      setEditLogs(data.logs || []);
      if (data.pagination) setLogsPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchProductsSearch = async ({
    page = pagination.page,
    per_page = pagination.per_page,
    category_id = categoryFilter,
    q = searchQuery,
  } = {}) => {
    // Abort any previous in-flight request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    setLoadingSearch(true);
    try {
      const token = await currentUser.getIdToken();
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      };

      const params = new URLSearchParams({ page, per_page });
      if (category_id) params.set("category_id", category_id);
      if (q) params.set("q", q);

      const productsResult = await axios.get(
        `${getApiUrl()}/api/v2/products/?${params.toString()}`,
        config,
      );

      // productsResult is the axios response directly (not Promise.allSettled)
      const data = productsResult.data;
      setProducts(data.products || []);
      if (data.pagination) setPagination(data.pagination);
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") {
        // Request was aborted by a newer request — ignore silently
        return;
      }
      console.error("Unexpected error:", error);
      setProducts([]);
    } finally {
      // Only clear loading if this controller is still the latest one
      if (searchAbortControllerRef.current === controller) {
        setLoadingSearch(false);
      }
    }
  };

  // Initial load only. fetchProductsAndLogs reads (and writes) pagination,
  // filters and categories, so listing it here would refetch in a loop —
  // later fetches are triggered explicitly by the search/pagination handlers.
  useEffect(() => {
    if (currentUser) {
      fetchProductsAndLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Animated Tab Button Component
  const TabButton = ({ tabKey, config, index }) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        onClick={() => {
          setActiveTab(tabKey);
        }}
        className={`relative flex flex-col md:flex-row items-center justify-center gap-1 sm:gap-2 md:gap-3 px-2 sm:px-3 md:px-4 lg:px-6 py-2 md:py-3 rounded-xl transition-all duration-200 flex-1 min-w-0 ${
          isActive
            ? "bg-orange-500 text-white"
            : "text-gray-600 hover:bg-slate-300"
        }`}
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={config.iconPath}
          />
        </svg>
        <span className="font-medium text-xs sm:text-sm truncate">
          {language === "zh" ? config.labelZh : config.labelEn}
        </span>
      </button>
    );
  };

  if (!currentUser || !profile) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
        <Sidebar language={language} />
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
        <div className="flex-1 flex flex-col gap-6 lg:gap-8 items-center justify-center -mt-20 lg:-mt-36 px-4">
          <div className="flex flex-col gap-2 lg:gap-4 items-center text-center">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"></div>
                <div
                  className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
              <span className="text-orange-600 dark:text-orange-500 text-base lg:text-lg font-semibold">
                {language === "zh" ? "正在加載數據..." : "Loading data..."}
              </span>
            </div>
            <div className="text-xs lg:text-sm text-orange-800 dark:text-orange-200">
              {language === "zh"
                ? "請稍候，我們正在為您處理繁重的工作。"
                : "Please wait while we do the heavy lifting for you."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans overflow-hidden">
      <Sidebar language={language} />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col overflow-hidden pt-12">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-5/6 mx-auto px-4 lg:px-8 py-8">
            {/* Header Section with decorative elements */}
            <div className="relative mb-8 lg:mb-12">
              {/* Background decorative element */}
              {/* <div className="absolute -top-10 -left-10 w-40 h-40 bg-orange-500/5 rounded-full blur-3xl" />
                            <div className="absolute -top-5 right-10 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl" /> */}

              <div className="relative text-center">
                {/* Icon badge */}
                {/* <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg shadow-orange-500/30 mb-4 animate-float">
                                    <svg
                                        className="w-8 h-8 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                        />
                                    </svg>
                                </div> */}

                <h1 className="text-3xl lg:text-4xl font-bold text-black dark:text-white mb-2">
                  {language === "zh"
                    ? "產品管理中心"
                    : "Product Management Center"}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                  {language === "zh"
                    ? "上傳、查看和管理您的產品信息"
                    : "Upload, view, and manage your product information"}
                </p>
              </div>
            </div>

            {/* Tab Navigation - Card Style */}
            <div className="flex justify-center justify-self-center mb-8 w-full">
              <div className="inline-flex gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 bg-gray-100 shadow-xl rounded-xl w-full max-w-full overflow-x-auto">
                {Object.entries(TAB_CONFIG_ADMIN).map(
                  ([key, config], index) => (
                    <TabButton
                      key={key}
                      tabKey={key}
                      config={config}
                      index={index}
                    />
                  ),
                )}
              </div>
            </div>

            {/* Tab Content with animation */}
            <div className="animate-fadeIn">
              {activeTab === "upload" && "upload" in TAB_CONFIG_ADMIN && (
                <UploadTab language={language} currentUser={currentUser} />
              )}
              {activeTab === "database" && (
                <div className="animate-slideUp">
                  {loading ? <p role="status" className="p-6 text-center text-orange-600">
                    {language === "zh" ? "正在載入產品…" : "Loading products…"}
                  </p> : <DatabaseView
                    language={language}
                    loading={loading}
                    products={products}
                    pagination={pagination}
                    categories={categories}
                    categoryFilter={categoryFilter}
                    searchQuery={searchQuery}
                    onCategoryChange={(id) => {
                      setCategoryFilter(id);
                      fetchProductsSearch({
                        page: 1,
                        category_id: id,
                      });
                    }}
                    loadingSearch={loadingSearch}
                    onSearchChange={(val) => {
                      setSearchQuery(val);
                      fetchProductsSearch({
                        page: 1,
                        q: val,
                      });
                    }}
                    onPageChange={(p) => fetchProductsSearch({ page: p })}
                    onProductDeleted={() => fetchProductsAndLogs({ page: 1 })}
                    onNavigateToUpload={() => setActiveTab("upload")}
                  />}
                </div>
              )}
              {activeTab === "logs" && "logs" in TAB_CONFIG_ADMIN && (
                <div className="animate-slideUp">
                  <EditHistory
                    language={language}
                    loading={loading || loadingLogs}
                    editLogs={editLogs}
                    pagination={logsPagination}
                    onPageChange={(p) => fetchLogs({ page: p })}
                  />
                </div>
              )}
              {activeTab === "duplicates" &&
                "duplicates" in TAB_CONFIG_ADMIN && (
                  <div className="animate-slideUp">
                    <ProductDuplicatesPanel
                      language={language}
                      onMergeComplete={fetchProductsAndLogs}
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
